/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IconButton } from '@fluentui/react/lib/Button';
import { ContextualMenuItemType, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { ITextField } from '@fluentui/react/lib/TextField';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisContextualMenu } from 'src/core/components/LuisContextualMenu';
import { LuisTextField, LuisTextFieldProps } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';

export type LuisContextualMenuInputProps = Omit<LuisTextFieldProps, 'onSubmit'> & {
    /**
     * The context menu item list.
     */
    menuItems: IContextualMenuItem[];

    /**
     * Callback for creating a new item.
     */
    onSubmit: (value: string) => void;

    /**
     * The initial value of the textField.
     */
    initValue?: string;

    /**
     * A flag to whether show the confirm
     * icon or not.
     */
    showConfirmIcon?: boolean;

    /**
     * The items to display when the filtered items
     * have a length of zero.
     */
    onNoItemsContent?: (query: string) => IContextualMenuItem[];

    /**
     * The items to display when the search query
     * doesn't fully match one item.
     */
    onNoExactQueryMatchContent?: (query: string) => IContextualMenuItem[];

    /**
     * Optional props to send to the button
     * that opens the menu.
     */
    menuTriggerProps?: Partial<IButtonProps>;

    /**
     * Optional props to pass to the underlying
     * contextual menu
     */
    contextualMenuProps?: Partial<IContextualMenuProps>;
};

const messages = defineMessages({
    placeholder: {
        id: 'core.luisContextualMenuInput.placeholder',
        defaultMessage: 'Search ...'
    },
    confirmAriaLabel: {
        id: 'core.luisContextualMenuInput.confirmAriaLabel',
        defaultMessage: 'Confirm'
    }
});

/**
 * The LuisContextualMenuInput component
 *
 * @param props The properties of the input field view.
 */
export const LuisContextualMenuInput = (props: LuisContextualMenuInputProps) => {
    const {
        menuItems,
        onNoItemsContent,
        onSubmit,
        initValue = '',
        showConfirmIcon = true,
        placeholder,
        menuTriggerProps,
        contextualMenuProps,
        onNoExactQueryMatchContent,
        onKeyDown,
        ...rest
    } = props;
    const [currentText, setCurrentText] = React.useState(initValue);
    const [isMenuFocused, setIsMenuFocused] = React.useState(false);
    const [isMenuVisible, setIsMenuVisible] = React.useState(false);
    const fieldRef = React.useRef<ITextField>(null);
    const fieldWrapperRef = React.useRef<HTMLDivElement>(null);
    const { localize } = useLocalization();

    React.useEffect(() => setCurrentText(initValue), [initValue]);

    let items = substringSearchStrategy(menuItems, currentText, 'text');

    if (items.length === 0 && onNoItemsContent) {
        items = items.concat(onNoItemsContent(currentText));
    }

    if (items.every(i => i.text.toLocaleLowerCase() !== currentText.toLocaleLowerCase()) && onNoExactQueryMatchContent) {
        items = items.concat(onNoExactQueryMatchContent(currentText));
    }

    /**
     * Follows the rules as follows:
     *
     * - If the key was `Escape`, close the contextual menu.
     *
     * - If `Up` or `Down` arrow keys are pressed, the menu
     * should be focused. However if the keys pressed are any
     * other ones, then the menu should not be focused to
     * continue typing in the field.
     */
    const onTextFieldKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === LuisKeyCodes.Escape && isMenuVisible) {
            setIsMenuVisible(false);
            event.stopPropagation();

            return;
        }

        if (!isMenuVisible) {
            setIsMenuVisible(true);
        }

        if (event.key === LuisKeyCodes.ArrowUp || event.key === LuisKeyCodes.ArrowDown) {
            setIsMenuFocused(true);
            event.stopPropagation();
        } else {
            setIsMenuFocused(false);
        }
    };

    /**
     * Fires when the contextual menu fires
     * a keyup event.
     *
     * @param event The event that was fired.
     */
    const onMenuKeyUp = (event: React.KeyboardEvent) => {
        if (event.key === LuisKeyCodes.ArrowRight || event.key === LuisKeyCodes.ArrowLeft) {
            setIsMenuFocused(false);
            fieldRef.current.focus();
        }
    };

    /**
     * Finds the first clickable item in the menu items
     * and simulates a click on it. If not found, then
     * the current text is sent over.
     */
    const onEnterKeyUp = () => {
        if (currentText === '') {
            return;
        }

        const item = items.find(i => i.itemType !== ContextualMenuItemType.Header && i.itemType !== ContextualMenuItemType.Divider);

        if (item && item.onClick) {
            item.onClick();
        } else {
            onSubmit(currentText);
        }

        dismissMenu();
    };

    /**
     * Ensures that the contextual menu is open
     * but does not take focus.
     */
    const onTextFieldFocus = () => {
        setIsMenuVisible(true);
        setIsMenuFocused(false);
    };

    /**
     * When the Fabric menu tries to dismiss:
     *
     * - If the event is a key event (`Escape`) key,
     * the menu should blur and the text field be
     * focused.
     *
     * - If the event is a mouse event, check if it
     * is clicking on the text field or not. If it
     * is clicking on the text field, nothing should
     * happen. If it is clicking outside the text field
     * then the menu should be closed and the text
     * field focused.
     *
     * @param event The event that was fired.
     */
    const onDismiss = (event: React.KeyboardEvent | React.MouseEvent) => {
        const isKeyEvent = (event as React.KeyboardEvent).key !== undefined;

        // If not key event, prevent dismissing the menu (default)
        if (!isKeyEvent) {
            event.preventDefault();
        }

        const clickOutsideMenu = !fieldWrapperRef.current.contains(event.target as Node);
        const focusEvent = event.type === 'focus';

        if (isKeyEvent) {
            setIsMenuVisible(false);
            setIsMenuFocused(false);
            fieldRef.current.focus();

            return;
        }

        if (clickOutsideMenu || focusEvent) {
            dismissMenu();
            fieldRef.current.blur();
        }
    };

    /**
     * Dismisses the menu by marking it
     * as not visible and not focused.
     */
    const dismissMenu = () => {
        setIsMenuVisible(false);
        setIsMenuFocused(false);
    };

    return (
        <Stack horizontal verticalFill verticalAlign="center">
            <StackItem verticalFill align="end">
                <LuisContextualMenu
                    items={items}
                    isVisible={isMenuVisible}
                    isFocused={isMenuFocused}
                    onDismiss={onDismiss}
                    onKeyUp={onMenuKeyUp}
                    menuTriggerProps={menuTriggerProps}
                    contextualMenuProps={contextualMenuProps}
                />
            </StackItem>
            <Stack.Item grow>
                <div ref={fieldWrapperRef}>
                    <LuisTextField
                        componentRef={fieldRef}
                        value={currentText}
                        placeholder={placeholder || localize(messages.placeholder)}
                        onEnterKeyUp={onEnterKeyUp}
                        onChange={(_, text) => setCurrentText(text)}
                        onFocus={onTextFieldFocus}
                        onKeyDown={event => {
                            onTextFieldKeyDown(event);
                            if (onKeyDown) {
                                onKeyDown(event);
                            }
                        }}
                        {...rest}
                    />
                </div>
            </Stack.Item>

            {showConfirmIcon && (
                <IconButton ariaLabel={localize(messages.confirmAriaLabel)} iconProps={{ iconName: 'CheckMark' }} onClick={onEnterKeyUp} />
            )}
        </Stack>
    );
};
